import { toast } from "sonner";
import { ArrowRight, Award } from "lucide-react";
import MissionShell from "../MissionShell";
import { Card, Field, BtnLink } from "@/components/kit";
import { BRAND } from "@/config/brand";
import { EVAL_CRITERIA, EVAL_CHOICES } from "../content";
import { useProgress } from "../store";
import { isDone, REQUIREMENTS, doneCount } from "../logic";

function Criterion({ c, idx, value, onChange }) {
  const choices = EVAL_CHOICES.filter((ch) => !ch.optionalOnly || c.optional);
  return (
    <div className="flex flex-col gap-3 border-b border-line py-4 last:border-0 sm:flex-row sm:items-center sm:justify-between" data-testid={`eval-criterion-${idx + 1}`}>
      <p className="font-medium text-ink">{c.label}{c.hint && <span className="font-normal text-soft">, {c.hint}</span>}</p>
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={c.label}>
        {choices.map((ch) => (
          <button key={ch.id} type="button" role="radio" aria-checked={value === ch.id} onClick={() => onChange(ch.id)} data-testid={`eval-${c.id}-${ch.id}`}
            className={`min-h-[40px] rounded-full border px-4 text-sm transition-colors ${value === ch.id ? (ch.id === "yes" ? "border-bx bg-bx text-white" : ch.id === "improve" ? "border-gold bg-gold-soft text-[#6B4F24]" : "border-soft bg-[#F1EDEA] text-ink") : "border-line bg-white text-ink hover:border-bx/40"}`}>
            {ch.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Congrats() {
  const { state } = useProgress();
  const all = doneCount(state) === 7;
  return (
    <section className="grain relative animate-fade-up overflow-hidden rounded-[28px] bg-bx px-6 py-12 text-center text-white sm:px-12" data-testid="congrats-section">
      <div className="relative z-10">
        <img src={BRAND.logoMark} alt="" className="mx-auto h-20 w-20 rounded-2xl bg-ivory object-cover ring-1 ring-gold/50" />
        <h2 className="mx-auto mt-6 max-w-xl text-3xl font-semibold sm:text-4xl">Bravo, tu as créé ta première vidéo UGC !</h2>
        <p className="mx-auto mt-4 max-w-lg leading-relaxed text-white/85">Tu es passée de l'idée à une création concrète. Tu peux maintenant continuer à t'entraîner et améliorer ton style.</p>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <BtnLink to="/resultats" variant="light" size="lg" data-testid="discover-recap-button">Découvrir mon récapitulatif<ArrowRight className="h-4 w-4" /></BtnLink>
          {all ? (
            <BtnLink to="/certificat" variant="gold" size="lg" data-testid="congrats-certificate-link"><Award className="h-4 w-4" />Mon certificat</BtnLink>
          ) : (
            <p className="text-sm text-white/75" data-testid="congrats-certificate-hint">{7 - doneCount(state) === 1 ? "Termine la dernière mission" : `Termine les ${7 - doneCount(state)} missions restantes`} pour obtenir ton certificat.</p>
          )}
        </div>
      </div>
    </section>
  );
}

export default function Mission7() {
  const { state, update, setIn, setCompleted } = useProgress();
  const m = state.m7;
  const done = isDone(state, 7);
  return (
    <MissionShell id={7} done={done} after={done ? <Congrats /> : null}
      validate={{ missing: REQUIREMENTS[7](state), label: "Terminer mon bilan", onClick: () => { setCompleted(7, true); toast.success("Bilan terminé. Bravo !"); } }}>
      <div className="space-y-5">
        <Card>
          <h3 className="text-xl font-medium text-ink">Checklist d'auto-évaluation</h3>
          <p className="mt-1 text-sm text-soft">Regarde ta vidéo, puis réponds honnêtement pour chaque point.</p>
          <div className="mt-2">
            {EVAL_CRITERIA.map((c, i) => <Criterion key={c.id} c={c} idx={i} value={m.evals[c.id]} onChange={(v) => setIn("m7", "evals", c.id, v)} />)}
          </div>
        </Card>
        <Card className="space-y-5">
          <h3 className="text-xl font-medium text-ink">Mon bilan</h3>
          <Field label="Mes points forts" value={m.strengths} onChange={(v) => update("m7", { strengths: v })} multiline rows={3} placeholder="Ce qui est réussi dans ma vidéo" testId="bilan-strengths-input" />
          <Field label="Mes améliorations" value={m.improvements} onChange={(v) => update("m7", { improvements: v })} multiline rows={3} placeholder="Ce que je ferai différemment la prochaine fois" testId="bilan-improvements-input" />
          <Field label="Ce que j'ai appris" value={m.learned} onChange={(v) => update("m7", { learned: v })} multiline rows={3} placeholder="Ce que ce parcours m'a appris" testId="bilan-learned-input" />
          <p className="text-xs text-soft">Enregistrement automatique dans ton compte.</p>
        </Card>
      </div>
    </MissionShell>
  );
}
